import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { useAuth } from '../contexts/AuthContext';

/**
 * Fetches user preferences (theme, background effect, language, etc.).
 * These are always needed — the query starts on mount and caches for the
 * entire session.
 */
export const useUserPreferences = () => {
  const { currentUser, targetEmail } = useAuth();
  const userId = currentUser?.uid;
  const impersonatedEmail = targetEmail && targetEmail !== currentUser?.email ? targetEmail : null;

  return useQuery({
    queryKey: queryKeys.preferences.user(impersonatedEmail || userId || ''),
    queryFn: () => {
      if (!userId) {
        return {} as Record<string, any>;
      }
      return appwriteQueryFns.preferences.user(userId);
    },
    // Always enabled — queryFn handles missing identity gracefully.
    // When userId resolves, the key changes and a real fetch fires.
    enabled: true,
    // 5 min — mounted in AppLayout (every page); a 30s window re-fired
    // /account/prefs on every navigation. The auth-service cache absorbs
    // concurrent boot calls; this window avoids needless refetches.
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    placeholderData: (previousData: any) => previousData,
  });
};